/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { X, FileCode, AlertCircle, Zap } from 'lucide-react'
import { smartChatCompletion } from '@/providers/llm-provider'
import { useProjectStore } from '@/ui/stores/project-store'
import { getDirectoryHandle } from '@/lib/storage'
import { writeFile } from '@/lib/filesystem'

interface FilePreviewModalProps {
  files: Array<{ path: string; content: string }>
  onConfirm: (editedFiles: Array<{ path: string; content: string }>) => void
  onCancel: () => void
  onFilesChanged?: () => void // Callback to trigger file list refresh
}

export function FilePreviewModal({ files, onConfirm, onCancel, onFilesChanged }: FilePreviewModalProps) {
  const [editedFiles, setEditedFiles] = useState(files)
  const [selectedFileIndex, setSelectedFileIndex] = useState(0)
  const [isCompressing, setIsCompressing] = useState(false)
  const [compressingFileIndex, setCompressingFileIndex] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { currentProject } = useProjectStore()

  const selectedFile = editedFiles[selectedFileIndex]

  // Calculate total tokens (rough estimate: 1 token ≈ 4 characters)
  const totalChars = editedFiles.reduce((sum, file) => sum + file.content.length, 0)
  const estimatedTokens = Math.ceil(totalChars / 4)
  const isLargeContext = estimatedTokens > 100000

  // Save compressed .moth files to disk
  const saveCompressedFiles = async () => {
    if (!currentProject?.fileSystemPath) {
      console.log('[MOTHify] No file system path, skipping save to disk')
      return
    }

    try {
      const dirHandle = await getDirectoryHandle(currentProject.id)
      if (!dirHandle) {
        console.log('[MOTHify] No directory handle, skipping save to disk')
        return
      }

      // Save all .moth files that were compressed (have .moth extension)
      const mothFiles = editedFiles.filter(f => f.path.endsWith('.moth'))
      if (mothFiles.length === 0) {
        console.log('[MOTHify] No .moth files to save')
        return
      }

      console.log(`[MOTHify] Saving ${mothFiles.length} compressed .moth file(s) to disk...`)

      for (const file of mothFiles) {
        const success = await writeFile(dirHandle, file.path, file.content)
        if (success) {
          console.log(`[MOTHify] ✅ Saved: ${file.path}`)
        } else {
          console.error(`[MOTHify] ❌ Failed to save: ${file.path}`)
        }
      }

      console.log('[MOTHify] Finished saving compressed files to disk')

      // Trigger file list refresh after saving
      onFilesChanged?.()
    } catch (error) {
      console.error('[MOTHify] Error saving compressed files:', error)
    }
  }

  const handleConfirm = async () => {
    // Save compressed files to disk before confirming
    await saveCompressedFiles()
    onConfirm(editedFiles)
  }

  const handleContentChange = (newContent: string) => {
    setEditedFiles(prev =>
      prev.map((file, idx) =>
        idx === selectedFileIndex ? { ...file, content: newContent } : file
      )
    )
  }

  const handleRemoveFile = (index: number) => {
    const newFiles = editedFiles.filter((_, idx) => idx !== index)
    if (newFiles.length === 0) {
      onCancel()
      return
    }
    setEditedFiles(newFiles)
    if (selectedFileIndex >= newFiles.length) {
      setSelectedFileIndex(newFiles.length - 1)
    }
  }

  const handleMothifyFile = async (index: number) => {
    const file = editedFiles[index]

    console.log(`[MOTHify] Starting compression for "${file.path}"...`)

    // Check if file is already in MOTH format
    // MOTH format has very specific characteristics:
    // 1. File extension is .moth
    // 2. Has multiple [SECTION_NAME] headers at start of lines
    // 3. Sections are typically: [META], [CONTEXT], [FEATURES], [API], etc.
    // We'll use a stricter check: file must end with .moth AND have MOTH sections
    const hasMothExtension = file.path.endsWith('.moth')
    const mothSectionPattern = /^\[([A-Z_]+)\]$/gm
    const mothSections = file.content.match(mothSectionPattern)
    const hasMothSections = mothSections && mothSections.length >= 2

    // Only skip if BOTH extension is .moth AND has MOTH sections
    const isMothFormat = hasMothExtension && hasMothSections

    if (isMothFormat) {
      console.log(`[MOTHify] Skipping "${file.path}" - already in MOTH format (found ${mothSections!.length} sections: ${mothSections!.join(', ')})`)
      return
    }

    if (mothSections && mothSections.length >= 2) {
      console.log(`[MOTHify] File has ${mothSections.length} section-like patterns (${mothSections.join(', ')}), but not .moth extension - will compress anyway`)
    }

    console.log(`[MOTHify] File is not MOTH format, proceeding with compression...`)
    setIsCompressing(true)
    setCompressingFileIndex(index)

    try {
      // Get orchestrator model (or use default)
      const model = currentProject?.settings?.models?.orchestrator || 'anthropic/claude-3-5-sonnet-20241022'
      console.log(`[MOTHify] Using model: ${model}`)
      console.log(`[MOTHify] Using OpenRouter API key from Settings/localStorage`)

      // Simple MOTH compression prompt
      const mothifyPrompt = `You are a MOTH (Minimal Overhead Technical Hierarchy) compression expert. Convert the following document into compact MOTH format.

MOTH is 70-90% more compact than markdown. Use these principles:

## Core Syntax
- [SECTIONS] in ALL_CAPS denote logical blocks
- key:value is the primary data pattern
- Semicolons (;) separate list items
- {key=value} for state/config objects
- # for comments

## Guidelines
- Remove all fluff and prose
- Convert paragraphs to structured key:value pairs
- Group related information under [SECTION] headers
- Use semicolons for lists instead of bullet points
- Be ruthlessly concise while preserving all essential information
- Maintain technical accuracy

Now compress this document:

---
${file.content}
---

Return ONLY the compressed MOTH format. No explanations.`

      console.log(`[MOTHify] Sending compression request to API...`)
      const response = await smartChatCompletion({
        model,
        messages: [
          { role: 'user', content: mothifyPrompt }
        ],
        temperature: 0.3,
        maxTokens: 4000,
      })

      console.log(`[MOTHify] Compression successful! Original: ${file.content.length} chars, Compressed: ${response.content.length} chars`)

      // Update the file with compressed content and change extension to .moth
      const newPath = file.path.replace(/\.[^.]+$/, '.moth')
      console.log(`[MOTHify] Saving as: ${newPath}`)

      setEditedFiles(prev =>
        prev.map((f, idx) =>
          idx === index ? { ...f, path: newPath, content: response.content } : f
        )
      )
    } catch (error) {
      console.error('[MOTHify] Compression error:', error)
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error(`[MOTHify] Error details: ${errorMsg}`)
      setErrorMessage(`Failed to compress file: ${errorMsg}`)
    } finally {
      setIsCompressing(false)
      setCompressingFileIndex(null)
    }
  }

  const handleMothifyAll = async () => {
    console.log(`[MOTHify] Starting batch compression for ${editedFiles.length} files...`)

    // Compress all files sequentially, skipping those already in MOTH format
    for (let i = 0; i < editedFiles.length; i++) {
      const file = editedFiles[i]

      console.log(`[MOTHify] Processing file ${i + 1}/${editedFiles.length}: "${file.path}"`)

      // Check if file is already in MOTH format
      // Only skip if BOTH extension is .moth AND has MOTH sections
      const hasMothExtension = file.path.endsWith('.moth')
      const mothSectionPattern = /^\[([A-Z_]+)\]$/gm
      const mothSections = file.content.match(mothSectionPattern)
      const hasMothSections = mothSections && mothSections.length >= 2
      const isMothFormat = hasMothExtension && hasMothSections

      if (isMothFormat) {
        console.log(`[MOTHify] Skipping "${file.path}" - already in MOTH format (found ${mothSections.length} sections)`)
        continue
      }

      if (mothSections && mothSections.length >= 2) {
        console.log(`[MOTHify] File has ${mothSections.length} section-like patterns, but not .moth extension - will compress anyway`)
      }

      console.log(`[MOTHify] File is not MOTH format, proceeding with compression...`)
      setIsCompressing(true)
      setCompressingFileIndex(i)

      try {
        const model = currentProject?.settings?.models?.orchestrator || 'anthropic/claude-3-5-sonnet-20241022'
        console.log(`[MOTHify] Using model: ${model}`)
        console.log(`[MOTHify] Using OpenRouter API key from Settings/localStorage`)

        const mothifyPrompt = `You are a MOTH (Minimal Overhead Technical Hierarchy) compression expert. Convert the following document into compact MOTH format.

MOTH is 70-90% more compact than markdown. Use these principles:

## Core Syntax
- [SECTIONS] in ALL_CAPS denote logical blocks
- key:value is the primary data pattern
- Semicolons (;) separate list items
- {key=value} for state/config objects
- # for comments

## Guidelines
- Remove all fluff and prose
- Convert paragraphs to structured key:value pairs
- Group related information under [SECTION] headers
- Use semicolons for lists instead of bullet points
- Be ruthlessly concise while preserving all essential information
- Maintain technical accuracy

## Example Transformation
Before (verbose):
"""
The authentication system uses JWT tokens. When a user logs in, the server validates their credentials against the database. If valid, it generates a JWT token with the user's ID and role. The token expires after 24 hours.
"""

After (MOTH):
"""
[AUTH]
method:JWT
flow:login→validate_db→generate_token
token_payload:user_id;role
expiry:24h
"""

Now compress this document:

---
${file.content}
---

Return ONLY the compressed MOTH format. No explanations.`

        console.log(`[MOTHify] Sending compression request to API...`)
        const response = await smartChatCompletion({
          model,
          messages: [
            { role: 'user', content: mothifyPrompt }
          ],
          temperature: 0.3,
          maxTokens: 4000,
        })

        console.log(`[MOTHify] Compression successful! Original: ${file.content.length} chars, Compressed: ${response.content.length} chars`)

        // Update the file with compressed content and change extension to .moth
        const newPath = file.path.replace(/\.[^.]+$/, '.moth')
        console.log(`[MOTHify] Saving as: ${newPath}`)

        setEditedFiles(prev =>
          prev.map((f, idx) =>
            idx === i ? { ...f, path: newPath, content: response.content } : f
          )
        )
      } catch (error) {
        console.error('[MOTHify] Compression error:', error)
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        console.error(`[MOTHify] Error details: ${errorMsg}`)
        setErrorMessage(`Failed to compress file ${i + 1}/${editedFiles.length}: ${errorMsg}`)
        setIsCompressing(false)
        setCompressingFileIndex(null)
        return
      }
    }

    console.log(`[MOTHify] Batch compression complete!`)
    setIsCompressing(false)
    setCompressingFileIndex(null)
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="bg-background-secondary border border-border rounded-lg w-[80vw] max-w-[1400px] h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-border p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-title">Review Files Before Sending</h2>
            <p className="text-xs text-foreground-muted mt-1">
              Edit or trim file content to conserve context tokens
            </p>
          </div>
          <button onClick={onCancel} className="text-foreground-muted hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-500/10 border-b border-red-500/30 px-4 py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs text-red-500">{errorMessage}</span>
            </div>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-500 hover:text-red-400 text-lg leading-none"
            >
              ×
            </button>
          </div>
        )}

        {/* Context Warning */}
        {isLargeContext && (
          <div className="bg-yellow-500/10 border-b border-yellow-500/30 px-4 py-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-yellow-500">
              Large context detected (~{estimatedTokens.toLocaleString()} tokens). Consider trimming files or switching to a model with larger context window.
            </span>
          </div>
        )}

        {/* File Tabs */}
        <div className="border-b border-border px-4 py-2 flex items-center gap-2 overflow-x-auto">
          {editedFiles.map((file, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer transition-colors ${
                idx === selectedFileIndex
                  ? 'bg-accent/20 border border-accent'
                  : 'bg-background-tertiary hover:bg-background-tertiary/70'
              }`}
              onClick={() => setSelectedFileIndex(idx)}
            >
              <FileCode className="w-3 h-3" />
              <span className="text-xs font-mono truncate max-w-[200px]">
                {file.path.split('/').pop()}
              </span>
              <span className="text-[10px] text-foreground-muted">
                ({Math.ceil(file.content.length / 4).toLocaleString()} tokens)
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveFile(idx)
                }}
                className="text-foreground-muted hover:text-red-500 ml-1"
                title="Remove file"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>

        {/* File Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 py-2 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileCode className="w-4 h-4 text-accent" />
              <span className="text-sm font-mono">{selectedFile.path}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleMothifyFile(selectedFileIndex)}
                disabled={isCompressing}
                className="btn-secondary px-3 py-1 text-xs flex items-center gap-1.5 disabled:opacity-50"
                title="Compress this file using MOTH format (70-90% reduction)"
              >
                <Zap className="w-3 h-3" />
                {isCompressing && compressingFileIndex === selectedFileIndex ? 'Compressing...' : 'Compress with MOTHify'}
              </button>
              <div className="text-xs text-foreground-muted">
                {selectedFile.content.split('\n').length} lines • {selectedFile.content.length.toLocaleString()} chars • ~{Math.ceil(selectedFile.content.length / 4).toLocaleString()} tokens
              </div>
            </div>
          </div>

          <textarea
            value={selectedFile.content}
            onChange={(e) => handleContentChange(e.target.value)}
            className="flex-1 bg-background-tertiary text-foreground font-mono text-xs p-4 resize-none focus:outline-none"
            placeholder="File content..."
            spellCheck={false}
          />
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-xs text-foreground-muted">
              <strong>{editedFiles.length}</strong> file{editedFiles.length !== 1 ? 's' : ''} •
              <strong className={isLargeContext ? 'text-yellow-500 ml-1' : 'ml-1'}>
                ~{estimatedTokens.toLocaleString()} tokens
              </strong> total
            </div>
            {editedFiles.length > 1 && (
              <button
                onClick={handleMothifyAll}
                disabled={isCompressing}
                className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50"
                title="Compress all files using MOTH format"
              >
                <Zap className="w-3 h-3" />
                {isCompressing ? 'Compressing...' : 'Compress All with MOTHify'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="btn-secondary px-4 py-2">
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="btn-primary px-4 py-2"
              disabled={isCompressing}
            >
              Send to Orchestrator
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
